import React from "react";
import { Network, GitBranch, AlertTriangle } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Card, Avatar, Button } from "../../components/primitives";
import { memberName } from "./teamHelpers";

// Two panels that turn the member list into org shape: which level people sit at,
// and who carries how many reports. Roughly half of orgs have no team structure
// configured and some have no managers assigned, so both carry a real empty state
// that points at the fix rather than rendering a blank card.

const STACK_MAX = 6;

function PanelHeader({ icon, title, subtitle }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3],
      padding: `${t.s[3]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: t.s[2], minWidth: 0 }}>
        <span style={{
          width: 24, height: 24, borderRadius: t.r.sm, display: "inline-flex", alignItems: "center",
          justifyContent: "center", background: t.c.surfaceMuted, color: t.c.textSecondary, flexShrink: 0, marginTop: 1,
        }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, color: t.c.text, lineHeight: 1.3 }}>{title}</div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: 1.45 }}>{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function PanelEmpty({ icon, title, description, action }) {
  const t = useDashTheme();
  return (
    <div style={{ padding: `${t.s[7]}px ${t.s[5]}px`, textAlign: "center" }}>
      <span style={{
        width: 40, height: 40, borderRadius: t.r.md, display: "inline-flex", alignItems: "center",
        justifyContent: "center", background: t.c.surfaceMuted, color: t.c.textTertiary, marginBottom: t.s[3],
      }}>{icon}</span>
      <div style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>{title}</div>
      <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 4, lineHeight: 1.5, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
        {description}
      </div>
      {action && <div style={{ marginTop: t.s[4] }}>{action}</div>}
    </div>
  );
}

// Overlapping faces. The ring is the card surface, so the stack reads as one
// group rather than a row of separate avatars.
function AvatarStack({ members, size = 26, max = STACK_MAX, onMember }) {
  const t = useDashTheme();
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", minWidth: 0 }}>
      {shown.map((u, i) => (
        <span
          key={u._id || u.id || i}
          title={memberName(u)}
          onClick={onMember ? (e) => { e.stopPropagation(); onMember(u); } : undefined}
          style={{
            marginLeft: i ? -7 : 0, border: `2px solid ${t.c.surface}`, borderRadius: 99,
            display: "inline-flex", cursor: onMember ? "pointer" : "default", flexShrink: 0,
          }}
        >
          <Avatar user={u} name={memberName(u)} size={size} />
        </span>
      ))}
      {rest > 0 && (
        <span style={{ marginLeft: 6, fontSize: t.font.size.xs, color: t.c.textTertiary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          +{rest}
        </span>
      )}
    </span>
  );
}

export function OrgLevelsPanel({ levels, hasStructure, onMember, onConfigure }) {
  const t = useDashTheme();
  const colors = [t.series[5], t.series[1], t.series[0], t.series[2], t.series[6], t.series[3]];

  return (
    <Card padding={0}>
      <PanelHeader
        icon={<Network size={14} />}
        title="Org levels"
        subtitle="From your team structure — who sits at each level"
      />
      {/* With no structure configured, orgLevels lumps everyone into a single
          "Unassigned" row — one grey bar that says nothing. Those orgs get the
          set-up prompt instead. */}
      {(!hasStructure || levels.length === 0) ? (
        <PanelEmpty
          icon={<Network size={20} />}
          title={hasStructure ? "No members at these levels" : "No team structure yet"}
          description={hasStructure
            ? "Nobody in the current filter maps onto a configured level."
            : "Define levels and positions in Settings, then assign each member a position to see the shape of your org here."}
          action={!hasStructure && onConfigure
            ? <Button variant="secondary" onClick={onConfigure}>Set up team structure</Button>
            : null}
        />
      ) : levels.map((lvl, i) => (
        <div
          key={lvl.name}
          style={{
            display: "flex", alignItems: "center", gap: t.s[4],
            padding: `${t.s[3]}px ${t.s[4]}px`,
            borderTop: i ? `1px solid ${t.c.border}` : "none",
          }}
        >
          <span style={{ width: 112, flexShrink: 0, display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <span style={{ width: 3, height: 26, borderRadius: 2, background: lvl.isGap ? t.c.borderStrong : colors[i % colors.length], flexShrink: 0 }} />
            <span
              title={lvl.name}
              style={{
                fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, letterSpacing: "0.04em",
                textTransform: "uppercase", color: t.c.textTertiary,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >{lvl.name}</span>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <AvatarStack members={lvl.members} onMember={onMember} />
          </span>
          <span style={{
            fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold, color: t.c.text,
            fontVariantNumeric: "tabular-nums", width: 26, textAlign: "right", flexShrink: 0,
          }}>{lvl.members.length}</span>
        </div>
      ))}
    </Card>
  );
}

export function ReportingLinesPanel({ spans, orphanCount, onMember, onShowOrphans }) {
  const t = useDashTheme();
  const maxSpan = spans.reduce((n, s) => Math.max(n, s.reports.length), 0) || 1;

  return (
    <Card padding={0}>
      <PanelHeader
        icon={<GitBranch size={14} />}
        title="Reporting lines"
        subtitle="Direct reports per manager — spot the overloaded and the unassigned"
      />
      {spans.length === 0 ? (
        <PanelEmpty
          icon={<GitBranch size={20} />}
          title="No reporting lines yet"
          description="Assign each member a reporting manager and their team will appear here."
        />
      ) : spans.map((s, i) => (
        <div
          key={s.id}
          style={{
            display: "flex", alignItems: "center", gap: t.s[3],
            padding: `${t.s[3] - 1}px ${t.s[4]}px`,
            borderTop: i ? `1px solid ${t.c.border}` : "none",
          }}
        >
          <span
            onClick={onMember ? () => onMember(s.manager) : undefined}
            style={{ display: "inline-flex", flexShrink: 0, cursor: onMember ? "pointer" : "default" }}
          >
            <Avatar user={s.manager} name={memberName(s.manager)} size={30} />
          </span>
          <span style={{ width: 148, flexShrink: 0, minWidth: 0 }}>
            <span style={{
              display: "block", fontSize: t.font.size.md, fontWeight: t.font.weight.medium, color: t.c.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{memberName(s.manager)}</span>
            <span style={{
              display: "block", fontSize: t.font.size.xs, color: t.c.textTertiary,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{s.manager.position || "—"}</span>
          </span>
          <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 40, height: 6, borderRadius: 99, background: t.c.surfaceHover, overflow: "hidden" }}>
              <span style={{
                display: "block", height: "100%", borderRadius: 99,
                width: `${(s.reports.length / maxSpan) * 100}%`, background: t.c.primary,
              }} />
            </span>
            <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
              {s.reports.length} direct{s.reports.length === 1 ? "" : "s"}
            </span>
          </span>
          <span style={{ flexShrink: 0 }}>
            <AvatarStack members={s.reports} size={24} max={4} onMember={onMember} />
          </span>
        </div>
      ))}

      {orphanCount > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: `${t.s[3] - 1}px ${t.s[4]}px`, borderTop: `1px solid ${t.c.border}`,
          background: t.status.warningSoft, fontSize: t.font.size.sm, color: t.c.textSecondary,
        }}>
          <AlertTriangle size={14} style={{ color: t.status.warning, flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            <strong style={{ color: t.c.text, fontWeight: t.font.weight.semibold }}>
              {orphanCount} member{orphanCount === 1 ? "" : "s"}
            </strong>
            {orphanCount === 1 ? " reports" : " report"} to nobody — they won't appear in any manager's rollup.
          </span>
          {onShowOrphans && (
            <button
              type="button"
              onClick={onShowOrphans}
              style={{
                marginLeft: "auto", background: "none", border: "none", padding: 0, cursor: "pointer",
                color: t.c.primary, fontSize: t.font.size.sm, fontFamily: t.font.family, whiteSpace: "nowrap",
              }}
            >Show them</button>
          )}
        </div>
      )}
    </Card>
  );
}

export { AvatarStack };
